import type { OnboardingOrder } from '../hooks/use-enterprise-onboarding-steps';

// Single namespaced blob that holds all the small, non-reactive UI flags (tours, popups,
// one-shot signals). Larger reactive datasets (CV/LinkedIn fields, job queues, taxonomy
// caches) live under their own `j2l.*` keys built via `j2lStorageKeys` — cramming those
// arrays into this blob would re-serialize everything on every keystroke/poll.
const J2L_CACHE_KEY = 'j2l-cache';

// Bumped whenever the on-disk layout changes so `migrateLegacyLocalStorage` runs exactly once.
const SCHEMA_VERSION = 1;

// Namespace prefix for every standalone dataset key, so the whole app's footprint is greppable.
const NS = 'j2l';

type UserCache = {
  linkedinStepSeen?: boolean;
  enterpriseOnboardingOrder?: OnboardingOrder;
  serviceRequestsViewedAt?: Record<string, string>;
  bugReportTourSeen?: boolean;
  // UTC timestamp of the last time the "profile ready" incentive popup was shown to this user.
  incentivePopupShownAt?: string;
  // Whether the user dismissed the "availability matches" banner.
  availabilityMatchesBannerDismissed?: boolean;
  // ISO timestamp of a one-shot signal raised right before an email-verification redirect and
  // consumed on the next page load to show a toast. The verified state itself is re-derived from
  // the profile at read time, so only the presence and the age matter (see
  // `consumeEmailVerificationSignal`).
  emailVerificationSignalAt?: string;
};

type GlobalCache = {
  shortlistDemoTourSeen?: boolean;
};

type J2LCache = {
  version?: number;
  // Keyed by the OIDC user id (`sub`) — one entry per authenticated identity.
  users?: Record<string, UserCache>;
  global?: GlobalCache;
};

function getCache(): J2LCache {
  try {
    const raw = localStorage.getItem(J2L_CACHE_KEY);
    return raw ? (JSON.parse(raw) as J2LCache) : {};
  } catch (err) {
    console.warn('[J2LCache] Failed to parse cache:', err);
    return {};
  }
}

function saveCache(cache: J2LCache): void {
  try {
    localStorage.setItem(J2L_CACHE_KEY, JSON.stringify(cache));
  } catch (err) {
    console.error('[J2LCache] Failed to save cache:', err);
  }
}

function getUserCache(userId: string): UserCache {
  const cache = getCache();
  return cache.users?.[userId] ?? {};
}

function setUserCache(userId: string, userCache: UserCache): void {
  const cache = getCache();
  cache.users = cache.users ?? {};
  cache.users[userId] = userCache;
  saveCache(cache);
}

function getUserFlag<K extends keyof UserCache>(userId: string | undefined, key: K): UserCache[K] | undefined {
  if (!userId) return undefined;
  return getUserCache(userId)[key];
}

function setUserFlag<K extends keyof UserCache>(userId: string | undefined, key: K, value: UserCache[K]): void {
  if (!userId) return;
  const userCache = getUserCache(userId);
  userCache[key] = value;
  setUserCache(userId, userCache);
}

function clearUserFlag<K extends keyof UserCache>(userId: string | undefined, key: K): void {
  if (!userId) return;
  const userCache = getUserCache(userId);
  delete userCache[key];
  setUserCache(userId, userCache);
}

function getGlobalCache(): GlobalCache {
  const cache = getCache();
  return cache.global ?? {};
}

function setGlobalCache(globalCache: GlobalCache): void {
  const cache = getCache();
  cache.global = globalCache;
  saveCache(cache);
}

function setGlobalFlag<K extends keyof GlobalCache>(key: K, value: GlobalCache[K]): void {
  const globalCache = getGlobalCache();
  globalCache[key] = value;
  setGlobalCache(globalCache);
}

export function getShortlistDemoTourSeen(): boolean {
  return getGlobalCache().shortlistDemoTourSeen === true;
}

export function setShortlistDemoTourSeen(): void {
  setGlobalFlag('shortlistDemoTourSeen', true);
}

// --- Email-verification one-shot signal -----------------------------------------------------

// For a user who is already onboarded the signal is raised on one screen and consumed by the next,
// so its real lifetime is seconds. Not so mid-onboarding: of everywhere `default-user-page` sends a
// visit, the consumers are the two profile layouts, which covers every `/services/*` and
// `/enterprises/*` destination, plus `/onboarding/registration-completed`. The `/onboarding/*`
// wizards (`create-service-profile`, `create-enterprise-profile`, `create-enterprise-from-shortlist`,
// `linkedin-url`, `verify-email`) hang off their own layout and consume nothing. Filling one of
// those takes as long as it takes. An hour covers the form filled in one sitting; past that the
// signal expires there too, and what is lost is an informational toast. The ceiling is what stops
// it becoming a stray toast weeks later; the per-user scope is what keeps even a late one with its
// own owner.
const EMAIL_VERIFICATION_SIGNAL_MAX_AGE_MS = 60 * 60 * 1000;

// Scoped per user on purpose: a sign-out fits between raising it and consuming it, and the toast's
// wording is decided by the *consumer's* `emailValidated`, so a signal left behind by someone else
// surfaces as an "Error al verificar el correo" nobody asked for.
export function setEmailVerificationSignal(userId: string | undefined): void {
  setUserFlag(userId, 'emailVerificationSignalAt', new Date().toISOString());
}

// Returns whether a fresh email-verification signal was pending, clearing it in the process so the
// toast is shown exactly once. A stale one is cleared too, rather than left to fire later.
export function consumeEmailVerificationSignal(userId: string | undefined): boolean {
  const raisedAt = getUserFlag(userId, 'emailVerificationSignalAt');
  if (raisedAt === undefined) return false;

  clearUserFlag(userId, 'emailVerificationSignalAt');

  // The distance is absolute, unlike the `ageMs >= 0` in `getCachedJson`: that one reads without
  // destroying and the caller comes back, while by this point the flag is already gone, so turning
  // down a clock corrected backwards would throw away a legitimate signal with no second try. A
  // clock far ahead is suspect in its own direction and this turns it down just the same. No
  // separate `NaN` guard: a stamp that doesn't parse makes the comparison false by itself.
  const raisedAtMs = new Date(raisedAt).getTime();
  return Math.abs(Date.now() - raisedAtMs) < EMAIL_VERIFICATION_SIGNAL_MAX_AGE_MS;
}

// --- Enterprise onboarding: LinkedIn step -----------------------------------------------------

export function getLinkedinStepSeen(userId: string | undefined): boolean {
  return getUserFlag(userId, 'linkedinStepSeen') === true;
}

export function setLinkedinStepSeen(userId: string | undefined, seen = true): void {
  setUserFlag(userId, 'linkedinStepSeen', seen);
}

// --- Enterprise onboarding: step order --------------------------------------------------------

export function getEnterpriseOnboardingOrder(userId: string | undefined): OnboardingOrder | undefined {
  return getUserFlag(userId, 'enterpriseOnboardingOrder');
}

export function setEnterpriseOnboardingOrder(userId: string | undefined, order: OnboardingOrder): void {
  setUserFlag(userId, 'enterpriseOnboardingOrder', order);
}

// --- Service requests: "new matches" viewed-at map --------------------------------------------

export function getServiceRequestsViewedAt(userId: string | undefined): Record<string, string> | undefined {
  return getUserFlag(userId, 'serviceRequestsViewedAt');
}

export function setServiceRequestsViewedAt(userId: string | undefined, viewedAt: Record<string, string>): void {
  setUserFlag(userId, 'serviceRequestsViewedAt', viewedAt);
}

// --- Bug-report tour --------------------------------------------------------------------------

export function getBugReportTourSeen(userId: string | undefined): boolean {
  return getUserFlag(userId, 'bugReportTourSeen') === true;
}

export function setBugReportTourSeen(userId: string | undefined, seen = true): void {
  setUserFlag(userId, 'bugReportTourSeen', seen);
}

// --- "Profile ready" incentive popup --------------------------------------------------------

export function getIncentivePopupShownAt(userId: string | undefined): string | undefined {
  return getUserFlag(userId, 'incentivePopupShownAt');
}

export function setIncentivePopupShownAt(userId: string | undefined, shownAt: string): void {
  setUserFlag(userId, 'incentivePopupShownAt', shownAt);
}

// --- Availability "matches" banner ----------------------------------------------------------

export function getAvailabilityMatchesBannerDismissed(userId: string | undefined): boolean {
  return getUserFlag(userId, 'availabilityMatchesBannerDismissed') === true;
}

export function setAvailabilityMatchesBannerDismissed(userId: string | undefined): void {
  setUserFlag(userId, 'availabilityMatchesBannerDismissed', true);
}

// --- Standalone dataset keys ----------------------------------------------------------------

// Every large/reactive dataset persisted outside the `j2l-cache` blob gets its key here so the
// naming stays consistent (`j2l.<area>.<dataset>.<scope>`) and lives in one place.
export const j2lStorageKeys = {
  cvAutocompletedFields: (userId: string) => `${NS}.person.cv-autocompleted-fields.${userId}`,
  cvAnalysisJobs: (userId: string) => `${NS}.person.cv-analysis-jobs.${userId}`,
  enterpriseAutocompletedFields: (userId: string) => `${NS}.enterprise.linkedin-autocompleted-fields.${userId}`,
  enterpriseCompanyAnalysisJobs: (userId: string) => `${NS}.enterprise.linkedin-company-jobs.${userId}`,
  enterpriseJobPostAnalysisJobs: (userId: string) => `${NS}.enterprise.linkedin-jobpost-jobs.${userId}`,
  countriesTaxonomy: () => `${NS}.taxonomy.countries`
} as const;

// --- Generic TTL-backed JSON cache ----------------------------------------------------------

type CacheEnvelope<T> = { savedAt: string; value: T };

// Reads a JSON value written by `setCachedJson`, returning undefined when absent, unparseable, or
// older than `maxAgeMs`. Centralizes the freshness check so callers don't reinvent (and misconfigure)
// their own TTL logic.
export function getCachedJson<T>(key: string, maxAgeMs: number): T | undefined {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return undefined;
    const envelope = JSON.parse(raw) as CacheEnvelope<T>;
    if (!envelope?.savedAt) return undefined;
    // A `savedAt` that doesn't parse yields NaN, and a clock that ran ahead yields a negative age;
    // both compare false against `maxAgeMs`, so without these guards the entry would never expire.
    const savedAtMs = new Date(envelope.savedAt).getTime();
    const ageMs = Date.now() - savedAtMs;
    if (Number.isNaN(savedAtMs) || ageMs < 0 || ageMs >= maxAgeMs) return undefined;
    return envelope.value;
  } catch (err) {
    console.warn(`[J2LCache] Failed to read cached json for "${key}":`, err);
    return undefined;
  }
}

export function setCachedJson<T>(key: string, value: T): void {
  try {
    localStorage.setItem(key, JSON.stringify({ savedAt: new Date().toISOString(), value } as CacheEnvelope<T>));
  } catch (err) {
    console.error(`[J2LCache] Failed to write cached json for "${key}":`, err);
  }
}

// --- One-time migration / cleanup of the legacy scattered keys ------------------------------

// Renames the per-user dataset keys to their `j2l.*` equivalents so in-flight analyses keep
// polling, then drops the transient legacy flags outright. Runs once, gated on SCHEMA_VERSION.
export function migrateLegacyLocalStorage(): void {
  try {
    const cache = getCache();
    if ((cache.version ?? 0) >= SCHEMA_VERSION) return;

    const legacyDatasetRenames: { legacyPrefix: string; buildKey: (scope: string) => string }[] = [
      { legacyPrefix: 'cv_autocompleted_fields_', buildKey: j2lStorageKeys.cvAutocompletedFields },
      { legacyPrefix: 'cv_analysis_jobs_', buildKey: j2lStorageKeys.cvAnalysisJobs },
      { legacyPrefix: 'autocompleted_fields_', buildKey: j2lStorageKeys.enterpriseAutocompletedFields },
      { legacyPrefix: 'linkedin_company_analysis_jobs_', buildKey: j2lStorageKeys.enterpriseCompanyAnalysisJobs },
      { legacyPrefix: 'linkedin_job_post_analysis_jobs_', buildKey: j2lStorageKeys.enterpriseJobPostAnalysisJobs }
    ];

    // Transient standalone caches with no value worth preserving — matched by exact key so we
    // never clobber an unrelated key that merely shares a prefix (e.g. the old shared `temp`
    // job-queue bucket vs. some future `template…` key).
    const legacyExactKeys = ['emailVerifiedStatus', 'J2L.CACHE.COUNTRIES', 'temp'];

    // Per-scope flags whose key ends in a `<userId>`/`<profileId>` suffix — matched by prefix.
    const legacyFlagPrefixes = ['incentive_popup_', 'availability_matches_banner_dismissed_'];

    for (const key of Object.keys(localStorage)) {
      const rename = legacyDatasetRenames.find((r) => key.startsWith(r.legacyPrefix));
      if (rename) {
        const scope = key.slice(rename.legacyPrefix.length);
        const value = localStorage.getItem(key);
        if (value !== null) localStorage.setItem(rename.buildKey(scope), value);
        localStorage.removeItem(key);
        continue;
      }

      if (legacyExactKeys.includes(key) || legacyFlagPrefixes.some((prefix) => key.startsWith(prefix))) {
        localStorage.removeItem(key);
      }
    }

    cache.version = SCHEMA_VERSION;
    saveCache(cache);
  } catch (err) {
    console.error('[J2LCache] Legacy localStorage migration failed:', err);
  }
}
